export interface DayGridTimedItem {
  id: string;
  hour: number;
  duration: number;
}

export interface DayGridLayoutItem<T extends DayGridTimedItem> extends DayGridTimedItem {
  lane: number;
  laneCount: number;
  item: T;
}

function endHour(item: DayGridTimedItem): number {
  return item.hour + item.duration;
}

function overlaps(a: DayGridTimedItem, b: DayGridTimedItem): boolean {
  return a.hour < endHour(b) && b.hour < endHour(a);
}

function expandOverlapCluster(seed: DayGridTimedItem, all: DayGridTimedItem[]): DayGridTimedItem[] {
  const cluster = new Set<string>([seed.id]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const appt of all) {
      if (cluster.has(appt.id)) continue;
      for (const memberId of cluster) {
        const member = all.find((a) => a.id === memberId);
        if (member && overlaps(appt, member)) {
          cluster.add(appt.id);
          changed = true;
          break;
        }
      }
    }
  }
  return all.filter((a) => cluster.has(a.id));
}

/** Side-by-side lanes for overlapping appointments within one provider column. */
export function layoutDayColumnAppointments<T extends DayGridTimedItem>(
  appointments: T[],
): DayGridLayoutItem<T>[] {
  if (appointments.length === 0) return [];

  const sorted = [...appointments].sort((a, b) => {
    if (a.hour !== b.hour) return a.hour - b.hour;
    return b.duration - a.duration;
  });

  const laneEnds: number[] = [];
  const placed: Array<{ appt: T; lane: number }> = [];

  for (const appt of sorted) {
    let lane = laneEnds.findIndex((end) => end <= appt.hour);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(endHour(appt));
    } else {
      laneEnds[lane] = endHour(appt);
    }
    placed.push({ appt, lane });
  }

  return placed.map(({ appt, lane }) => {
    const clusterIds = new Set(expandOverlapCluster(appt, sorted).map((a) => a.id));
    const clusterLanes = placed.filter(({ appt: other }) => clusterIds.has(other.id)).map(({ lane: l }) => l);
    const laneCount = Math.max(...clusterLanes, 0) + 1;
    return {
      id: appt.id,
      hour: appt.hour,
      duration: appt.duration,
      lane,
      laneCount,
      item: appt,
    };
  });
}
